import React, {useEffect, useState} from 'react';
import useThrottle from 'react-use/lib/useThrottle';

import {BillingPayer, StatData, SurveysGoal, SurveySlug} from 'client/common/types';
import {openLink} from 'client/common/utils/window';
import {
    usePayers,
    usePayForWalletInterview,
    usePayFromWalletInterview,
    useWallet
} from 'client/common/hooks/payment';
import {useSurveyInterviewCost} from 'client/common/hooks/survey';
import {useModal} from 'client/common/hooks/modal';
import {useSendGoalOnce} from 'client/common/hooks/metrika';
import CardPaymentForm from 'client/common/components/card-payment-form';

import './unlock-interview-popup.css';
import {PayerCountry, PaymentType} from 'client/common/components/card-payment-form/types';
import {canPayByWallet} from 'client/common/utils/price';
import UnlockInterviewView from 'client/common/components/unlock-interview-popup/unlock-interview-view/unlock-interview-view';
import {logError} from '@yandex-int/pythia-libs/lib/rum';
import {isPaymentAsIndividualAvailable} from 'client/common/utils/payment';
import {useUserCountryId} from 'client/common/hooks/user';

export type FormActionType = 'useExisting' | 'buy';

interface Props {
    slug?: SurveySlug;
    initialCount: number;
    onPaymentFinish: VoidFunction;
    formActionDefault?: FormActionType;
    customTitle?: string;
    customSubtitle?: string;
    className?: string;
    maxCount?: number;
    stat?: StatData;
    lockedUnfinishedInterviewsCount?: number;
    lockedFinishedInteviewsCount?: number;
    unlockedFinishedInterviewsCount?: number;
    unlockedInterviewsCount?: number;
}

// eslint-disable-next-line max-statements, complexity
export default function UnlockInterviewPopup(props: Props) {
    const {
        slug = '',
        initialCount,
        onPaymentFinish,
        maxCount,
        customSubtitle,
        formActionDefault,
        customTitle,
        className,
        stat,
        lockedUnfinishedInterviewsCount,
        lockedFinishedInteviewsCount,
        unlockedFinishedInterviewsCount = 0,
        unlockedInterviewsCount = 0
    } = props;
    const [disabled, setDisabled] = useState(false);

    const [showModal, hideModal] = useModal();

    const [isFinishedInteviewsActivated, setIsFinishedInteviewsActivated] = useState(false);

    const payers = usePayers();
    const userCountryId = useUserCountryId();
    const [payAs, setPayAs] = useState<PaymentType>(
        isPaymentAsIndividualAvailable(userCountryId) ? 'individual' : 'entity'
    );

    const [count, setCount] = useState<number>(initialCount);
    const [interviewTotal, setInterviewTotal] = useState(0);

    const surveyInterviewStartedCount = stat?.started_events || 0;
    const surveyInterviewsFinishedCount = stat?.finished_events || 0;

    useEffect(() => {
        if (isFinishedInteviewsActivated) {
            setCount(surveyInterviewStartedCount - unlockedInterviewsCount);
            setInterviewTotal(surveyInterviewStartedCount - unlockedInterviewsCount);
        } else {
            setCount(surveyInterviewsFinishedCount - unlockedFinishedInterviewsCount);
            setInterviewTotal(surveyInterviewsFinishedCount - unlockedFinishedInterviewsCount!);
        }
    }, [
        isFinishedInteviewsActivated,
        surveyInterviewStartedCount,
        unlockedFinishedInterviewsCount,
        unlockedInterviewsCount,
        surveyInterviewsFinishedCount
    ]);

    const [formAction, setFormAction] = useState<FormActionType>(
        formActionDefault ? formActionDefault : 'useExisting'
    );

    const {data: walletData} = useWallet();

    const payFromInterviewsWallet = usePayFromWalletInterview();

    const THROTTLE_WAIT = 1000; // как часто отправлять запрос за ценой интервью
    const throttledInterviewCount = useThrottle(count, THROTTLE_WAIT);
    const sendGoalOnce = useSendGoalOnce();

    const handleChangeInterviewCount = (e: React.ChangeEvent<HTMLInputElement>) => {
        sendGoalOnce(SurveysGoal.UnlockInterviewPopupChangeInterviewCount);
        const value = parseInt(e.target.value, 10);

        setDisabled(Boolean(!value));

        setCount(value);
    };

    const {status, data: interviewCostData} = useSurveyInterviewCost(throttledInterviewCount || 0);

    const payForInterview = usePayForWalletInterview();

    const handlePaymentFormFinish = () => {
        hideModal();

        onPaymentFinish();
    };

    const handleCreatePaymentLink = async (payer: BillingPayer) => {
        return payForInterview({amount: throttledInterviewCount, payer});
    };

    const handleClickPay = async () => {
        sendGoalOnce(SurveysGoal.UnlockInterviewPopupClickPay);
        setDisabled(true);

        const link = await payForInterview({amount: throttledInterviewCount});

        openLink(link);
    };

    const handleClickPayFromInterviewsWallet = async () => {
        setDisabled(true);

        let finished = 0;
        let unfinished = 0;

        if (count <= surveyInterviewsFinishedCount - unlockedFinishedInterviewsCount) {
            finished = count;
            unfinished = 0;
        } else {
            finished = surveyInterviewsFinishedCount - unlockedFinishedInterviewsCount;
            unfinished = count - finished;
        }

        if (slug) {
            await payFromInterviewsWallet({
                finished,
                unfinished,
                slug
            });
        }
        setDisabled(false);
        handlePaymentFormFinish();
    };

    const handleClickPayIndividual = () => {
        sendGoalOnce(SurveysGoal.UnlockInterviewPopupClickPayIndividual);
        setDisabled(true);

        showModal(
            <CardPaymentForm
                createPaymentLink={handleCreatePaymentLink}
                onError={err => logError({message: 'Error when paying for interview', err})}
                onFinish={handlePaymentFormFinish}
                payers={payers.data}
                userCountry={String(userCountryId) as PayerCountry}
            />,
            {
                withCross: true,
                onClose: () => {
                    setDisabled(false);
                }
            }
        );
    };

    const handlePayingInterviewFromWallet = async (callback: () => Promise<any>) => {
        setDisabled(true);
        await callback();
        handlePaymentFormFinish();
    };

    const handleClickBillPay = async () => {
        if (payAs === 'individual') {
            return handleClickPayIndividual();
        }
        await handleClickPay();
    };

    const isInterviewCostLoaded = status === 'success' && interviewCostData;

    const ableToPay =
        isInterviewCostLoaded && walletData && canPayByWallet(interviewCostData.data, walletData);

    const costData = isInterviewCostLoaded && {
        vat: interviewCostData.data.vat,
        price: {
            value: interviewCostData.data.value,
            currency: interviewCostData.data.currency
        }
    };

    const balance = {
        balanceWithoutVat: walletData?.balance_without_vat,
        balanceWithVat: walletData?.balance_with_vat,
        currency: walletData?.currency
    };

    return (
        <UnlockInterviewView
            formAction={formAction}
            setFormAction={setFormAction}
            isFinishedInteviewsActivated={isFinishedInteviewsActivated}
            setIsFinishedInteviewsActivated={setIsFinishedInteviewsActivated}
            handleChangeInterviewCount={handleChangeInterviewCount}
            costReady={Boolean(isInterviewCostLoaded && walletData)}
            costData={costData}
            className={className}
            formActionDefault={formActionDefault}
            customTitle={customTitle}
            customSubtitle={customSubtitle}
            disabled={disabled}
            interviewTotal={interviewTotal}
            ableToPay={ableToPay}
            payAs={payAs}
            setPayAs={setPayAs}
            count={count}
            maxCount={maxCount}
            balance={balance}
            slug={slug}
            handleClickBillPay={handleClickBillPay}
            handleClickPayFromInterviewsWallet={handleClickPayFromInterviewsWallet}
            handlePayingInterviewFromWallet={handlePayingInterviewFromWallet}
            stat={stat}
            lockedUnfinishedInterviewsCount={lockedUnfinishedInterviewsCount}
            lockedFinishedInteviewsCount={lockedFinishedInteviewsCount}
            availablePaymentMethods={
                isPaymentAsIndividualAvailable(userCountryId)
                    ? ['individual', 'entity']
                    : ['entity']
            }
        />
    );
}
